import { useEffect, useState } from "react";
import { type Course, type Group, type Staff, Store, type StoreForReservation } from "../api/ApiData";

export type UTMParams = {
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  utmContent: string;
};

export type ReservationData = {
  rid: string | undefined;
  group: Group;
  utmParams: UTMParams;
  store: StoreForReservation | undefined;
  staff: Staff | null | undefined;
  course: Course | undefined;
  date: number | undefined;
  name: string;
  phone: string;
  mail: string;
  request: string;
  confirmed: boolean;
  posted: boolean;
  accepted: boolean;
  error: boolean;
  lineLoginUrl: string | null | undefined;
  fbc: string | undefined;
  fbp: string | undefined;
  rwg_token: string | undefined;
};

export type ReservationState = {
  data: ReservationData;
  setValue: <K extends keyof ReservationData>(key: K, value: ReservationData[K]) => void;
  form: 1 | 2 | 3 | "done" | "error";
  reset: () => void;
};

const useReservationState = (group: Group, utmParams: UTMParams, fbc?: string, fbp?: string, rwgToken?: string): ReservationState => {
  const defaultReservationData = {
    accepted: false,
    confirmed: false,
    course: undefined,
    date: undefined,
    error: false,
    fbc,
    fbp,
    group,
    lineLoginUrl: undefined,
    mail: "",
    name: "",
    phone: "",
    posted: false,
    request: "",
    rid: undefined,
    rwg_token: rwgToken,
    staff: undefined,
    store: undefined,
    utmParams,
  };
  const [data, setData] = useState<ReservationData>(defaultReservationData);

  useEffect(() => {
    setData((prevData) => ({ ...prevData, date: undefined }));
  }, [data.course?.id]);

  useEffect(() => {
    setData((prevData) => ({ ...prevData, date: undefined }));
  }, [data.staff?.id]);

  useEffect(() => {
    setData((prevData) => ({ ...prevData, course: undefined, staff: undefined }));
  }, [data.store?.id]);

  const setValue = <K extends keyof ReservationData>(key: K, value: ReservationData[K]) => {
    // 連続してsetValueを呼び出すと、古い呼び出し側が反映されないため、関数をsetDataに渡す
    setData((prevData) => ({ ...prevData, [key]: value }));
  };

  let form: 1 | 2 | 3 | "done" | "error" = 1;
  if (data.date) form = 2;
  if (data.confirmed) form = 3;
  if (data.accepted) form = "done";
  if (data.error) form = "error";

  const reset = () => setData({ ...defaultReservationData });

  return { data, form, reset, setValue };
};

export default useReservationState;
